import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  ArrowLeft, 
  User, 
  Building2, 
  Lock, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Loader2,
  AlertCircle,
  Save,
  BookOpen,
  Hash
} from 'lucide-react';
import axios from 'axios';
import { EditorNameHint, checkEditorName } from './EditorNameCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditUserPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState(null);
  const originalName = userData?.bibliotecaNome || '';
  const [formData, setFormData] = useState({
    // Credenciais
    email: '',
    // Informações do Administrador
    adminNome: '',
    adminCpf: '',
    adminDataNascimento: '',
    adminTelefone: '',
    adminEndereco: '',
    // Informações da Biblioteca
    bibliotecaNome: '',
    bibliotecaInstituicao: '',
    bibliotecaEndereco: '',
    bibliotecaEmail: '',
    bibliotecaTelefone: '',
    bibliotecaNumUsuarios: '',
    bibliotecaNumExemplares: ''
  });

  const fetchUser = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const user = response.data;
      setUserData(user);
      setFormData({
        email: user.email || '',
        adminNome: user.adminNome || '',
        adminCpf: user.adminCpf || '',
        adminDataNascimento: user.adminDataNascimento || '',
        adminTelefone: user.adminTelefone || '',
        adminEndereco: user.adminEndereco || '',
        bibliotecaNome: user.bibliotecaNome || '',
        bibliotecaInstituicao: user.bibliotecaInstituicao || '',
        bibliotecaEndereco: user.bibliotecaEndereco || '',
        bibliotecaEmail: user.bibliotecaEmail || '',
        bibliotecaTelefone: user.bibliotecaTelefone || '',
        bibliotecaNumUsuarios: user.bibliotecaNumUsuarios || '',
        bibliotecaNumExemplares: user.bibliotecaNumExemplares || ''
      });
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      setError('Erro ao carregar dados do usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('userData');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(user);
    if (parsedUser.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    
    fetchUser();
  }, [navigate, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      if (formData.bibliotecaNome.trim() && formData.bibliotecaNome.trim() !== originalName.trim()) {
        const nameErr = await checkEditorName(formData.bibliotecaNome.trim(), userId);
        if (nameErr) { setError(nameErr); return; }
      }
      await axios.put(`${API}/admin/users/${userId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Usuário atualizado com sucesso!');
      
      // Redirecionar após 1.5 segundos
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="edit-user-page">
        <div className="edit-user-loading">
          <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#6A68A3' }} />
          <p>Carregando dados do usuário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-page">
      {/* Header */}
      <header className="edit-user-header">
        <div className="edit-user-header-content">
          <Button 
            variant="ghost" 
            onClick={handleBack}
            className="back-button-inline"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </Button>
          <div className="header-title-group">
            <BookOpen className="w-8 h-8" style={{ color: '#68AAB0' }} />
            <span className="header-logo">
              Libr<span style={{ color: '#F75C6F' }}>ia</span>
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="edit-user-main">
        <div className="edit-user-container">
          <div className="edit-user-title-section">
            <h1 className="edit-user-title">Editar Usuário</h1>
            <p className="edit-user-subtitle">
              Altere as informações do usuário e clique em &quot;Salvar Alterações&quot;
            </p>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="form-message form-error-message">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}
          
          {success && (
            <div className="form-message form-success-message">
              <Save className="w-5 h-5" />
              {success}
            </div>
          )}

          {/* User Info Badge */}
          {userData && (
            <div className="user-info-badge">
              <span className="user-info-label">Usuário:</span>
              <span className="user-info-value">{userData.email}</span>
              {userData.isento && <span className="badge badge-info">Isento</span>}
              {!userData.isento && <span className="badge badge-default">{userData.planoId || 'N/A'}</span>}
            </div>
          )}

          <form onSubmit={handleSubmit} className="edit-user-form">
            {/* Credenciais de Acesso */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <Lock className="w-5 h-5" />
                  Credenciais de Acesso
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field full-width">
                  <Label htmlFor="email">
                    <Mail className="inline-icon" />
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Informações do Administrador */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <User className="w-5 h-5" />
                  Informações do Administrador
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field">
                  <Label htmlFor="adminNome">
                    <User className="inline-icon" />
                    Nome Completo
                  </Label>
                  <Input
                    id="adminNome"
                    name="adminNome"
                    value={formData.adminNome}
                    onChange={handleChange}
                    placeholder="Nome completo do administrador"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="adminCpf">
                    <Hash className="inline-icon" />
                    CPF
                  </Label>
                  <Input
                    id="adminCpf"
                    name="adminCpf"
                    value={formData.adminCpf}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="adminDataNascimento">
                    <Calendar className="inline-icon" />
                    Data de Nascimento
                  </Label>
                  <Input
                    id="adminDataNascimento"
                    name="adminDataNascimento"
                    type="date"
                    value={formData.adminDataNascimento}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="adminTelefone">
                    <Phone className="inline-icon" />
                    Telefone (Celular/WhatsApp)
                  </Label>
                  <Input
                    id="adminTelefone"
                    name="adminTelefone"
                    value={formData.adminTelefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="form-field full-width">
                  <Label htmlFor="adminEndereco">
                    <MapPin className="inline-icon" />
                    Endereço
                  </Label>
                  <Input
                    id="adminEndereco"
                    name="adminEndereco"
                    value={formData.adminEndereco}
                    onChange={handleChange}
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Informações da Biblioteca */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <Building2 className="w-5 h-5" />
                  Informações da Biblioteca
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field">
                  <Label htmlFor="bibliotecaNome">
                    <BookOpen className="inline-icon" />
                    Nome da Biblioteca
                  </Label>
                  <Input
                    id="bibliotecaNome"
                    name="bibliotecaNome"
                    value={formData.bibliotecaNome}
                    onChange={handleChange}
                    placeholder="Nome da biblioteca"
                    data-testid="admin-edit-editor-name-input"
                  />
                  <EditorNameHint nome={formData.bibliotecaNome} excludeId={userId} initial={originalName} />
                </div>
                <div className="form-field">
                  <Label htmlFor="bibliotecaInstituicao">
                    <Building2 className="inline-icon" />
                    Instituição
                  </Label>
                  <Input
                    id="bibliotecaInstituicao"
                    name="bibliotecaInstituicao"
                    value={formData.bibliotecaInstituicao}
                    onChange={handleChange}
                    placeholder="Nome da instituição"
                  />
                </div>
                <div className="form-field full-width">
                  <Label htmlFor="bibliotecaEndereco">
                    <MapPin className="inline-icon" />
                    Endereço da Biblioteca
                  </Label>
                  <Input
                    id="bibliotecaEndereco"
                    name="bibliotecaEndereco"
                    value={formData.bibliotecaEndereco}
                    onChange={handleChange}
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="bibliotecaEmail">
                    <Mail className="inline-icon" />
                    E-mail da Biblioteca
                  </Label>
                  <Input
                    id="bibliotecaEmail"
                    name="bibliotecaEmail"
                    type="email"
                    value={formData.bibliotecaEmail}
                    onChange={handleChange}
                    placeholder="biblioteca@exemplo.com"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="bibliotecaTelefone">
                    <Phone className="inline-icon" />
                    Telefone Comercial
                  </Label>
                  <Input
                    id="bibliotecaTelefone"
                    name="bibliotecaTelefone"
                    value={formData.bibliotecaTelefone}
                    onChange={handleChange}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="bibliotecaNumUsuarios">
                    <User className="inline-icon" />
                    Número de Usuários (aproximado)
                  </Label>
                  <Input
                    id="bibliotecaNumUsuarios"
                    name="bibliotecaNumUsuarios"
                    type="number"
                    value={formData.bibliotecaNumUsuarios}
                    onChange={handleChange}
                    placeholder="Ex: 500"
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="bibliotecaNumExemplares">
                    <BookOpen className="inline-icon" />
                    Número de Exemplares (aproximado)
                  </Label>
                  <Input
                    id="bibliotecaNumExemplares"
                    name="bibliotecaNumExemplares"
                    type="number"
                    value={formData.bibliotecaNumExemplares}
                    onChange={handleChange}
                    placeholder="Ex: 10000"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="edit-user-actions">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="btn-secondary-custom"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="btn-primary-custom"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditUserPage;
